import {
  AfterViewInit,
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { HttpImageUploaderProvider, IMAGEUPLOADER_PROVIDER_TOKEN } from '@rp/shared/providers';

import { SpinnerComponent } from '../../spinner';
import { NoImageComponent } from '../no-image/no-image.component';
import { NoImageSize } from '../models';

@Directive({
  selector: '[rpLazyLoadImage]',
  standalone: true,
  providers: [{ provide: IMAGEUPLOADER_PROVIDER_TOKEN, useClass: HttpImageUploaderProvider }],
})
export class LazyLoadImageDirective implements OnInit, AfterViewInit, OnDestroy {
  size = input<NoImageSize>('s');
  title = input<string>();
  rpLazyLoadImage = input<string>('');

  private _imageUploaderProvider = inject(IMAGEUPLOADER_PROVIDER_TOKEN);
  private _spinnerRef: ComponentRef<SpinnerComponent> | null = null;
  private _noImageRef: ComponentRef<NoImageComponent> | null = null;
  private _el = inject(ElementRef);
  private _renderer = inject(Renderer2);
  private _viewContainerRef = inject(ViewContainerRef);
  private _destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this._renderer.setStyle(this._el.nativeElement, 'display', 'none');
  }

  ngAfterViewInit(): void {
    this._spinnerRef = this._viewContainerRef.createComponent(SpinnerComponent);
    this._spinnerRef.setInput('size', 'xs');
    this._renderer.setStyle(this._spinnerRef.location.nativeElement, 'padding', '0');

    this._imageUploaderProvider
      .getImageFromS3(this.rpLazyLoadImage())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: base64 => this._setImage(base64),
        error: () => this._setNoImage(),
      });
  }

  ngOnDestroy(): void {
    this._spinnerRef?.destroy();
  }

  private _setImage(base64: string): void {
    if (!base64) {
      this._setNoImage();

      return;
    }

    const imageElement = this._el.nativeElement;
    this._renderer.setAttribute(imageElement, 'src', base64);
    this._renderer.addClass(imageElement, 'has-image');
    imageElement.onload = () => {
      this._spinnerRef?.destroy();
      this._renderer.setStyle(imageElement, 'display', 'block');
    };
  }

  private _setNoImage(): void {
    this._noImageRef = this._viewContainerRef.createComponent(NoImageComponent);
    this._noImageRef.setInput('size', this.size());

    if (this.title()) {
      this._noImageRef.setInput('title', this.title());
    }

    this._spinnerRef?.destroy();
  }
}
